import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, EmptyState, Icon, Tabs, TabsContent, TabsList, TabsTrigger, toast, type IconName } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { isPlanGateError } from "../shared/plan-gate";
import type { SourceView } from "../shared/types";
import { useAudienceLabel, useBillingOverview } from "../shared/use-shared-queries";
import { AgentPicker } from "./agent-picker";
import { AudiencePicker } from "./audience-picker";
import { FilesPanel } from "./files-panel";
import { defaultAudiencesFor, exposesToGuests } from "./source-model";
import { QaPanel, TextPanel } from "./text-panels";
import { useCreateSource, type NewSource } from "./use-knowledge";
import type { useUploads } from "./use-uploads";
import { WebsitePanel } from "./website-panel";

const K = "deployKnowledge.knowledge.add";
const TABS: readonly { value: AddTab; icon: IconName; label: string }[] = [
  { value: "files", icon: "upload", label: "tabFiles" },
  { value: "website", icon: "globe", label: "tabWebsite" },
  { value: "qa", icon: "message-square", label: "tabQa" },
  { value: "text", icon: "quote", label: "tabText" },
  { value: "drive", icon: "database", label: "tabDrive" },
];
type AddTab = "files" | "website" | "qa" | "text" | "drive";
const PUBLIC_AUDIENCE = 1;
const MEMBERS_AUDIENCE = 3;
const formIdOf = (tab: AddTab) => `add-source-${tab}`;

interface AddSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploads: ReturnType<typeof useUploads>;
  sources: readonly SourceView[];
  /** Agents preselected in "Use in agents" (the page's agent, when the member may edit it). */
  agentIds: readonly string[];
  /** A 402 came back: the page decides between "storage full" and "needs a higher plan". */
  onPlanGate: (error: unknown) => void;
}

function StorageLeft() {
  const { t } = useTranslation();
  const billing = useBillingOverview();
  const meter = billing.data?.storage_mb;
  if (meter?.limit == null) return <span />;
  return (
    <span className="flex items-center gap-1.5 text-xs text-fg-subtle">
      <Icon name="database" className="size-3.5" />
      {t(`${K}.storageLeft`, { mb: Math.max(0, meter.limit - meter.used) })}
    </span>
  );
}

function withAgents(source: NewSource, agentIds: readonly string[]): NewSource {
  if (agentIds.length === 0) return source;
  return source.type === "website" ? { ...source, input: { ...source.input, agentIds } } : { ...source, agentIds };
}

function successMessage(source: NewSource, t: (key: string, options?: Record<string, unknown>) => string, pages: number | null): string {
  if (source.type === "qa") return t(`${K}.qaAdded`);
  if (source.type === "text") return t(`${K}.textAdded`);
  return pages === null ? t(`${K}.crawlQueuedPlain`) : t(`${K}.crawlQueued`, { count: pages });
}

/** "Add knowledge source": Files (real presigned uploads) · Website · Q&A · Text · Drive (not yet). */
export function AddSourceDialog({ open, onOpenChange, uploads, sources, agentIds, onPlanGate }: AddSourceDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const create = useCreateSource();
  const labelOf = useAudienceLabel();
  const permissions = usePermissions();
  const canPickAgents = !permissions.readOnly && permissions.can("agents.edit", { anywhere: true });
  const [tab, setTab] = useState<AddTab>("files");
  // Nothing chosen yet = the tab's own starting access: Everyone for a website, Signed-in users otherwise.
  const [chosen, setChosen] = useState<number[] | null>(null);
  const audiences = chosen ?? defaultAudiencesFor(tab);
  const [linkIds, setLinkIds] = useState<string[]>(() => [...agentIds]);
  const linked = canPickAgents ? linkIds : agentIds;
  const [pending, setPending] = useState<File[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [audienceError, setAudienceError] = useState<string | undefined>();
  const [pages, setPages] = useState<number | null>(null);

  const guardAudiences = () => {
    if (audiences.length > 0) return true;
    setAudienceError(t(`${K}.audiencesRequired`));
    return false;
  };
  const submitFiles = () => {
    if (pending.length === 0) {
      if (uploads.items.length > 0) onOpenChange(false);
      else setError(t(`${K}.filesRequired`));
      return;
    }
    if (!guardAudiences()) return;
    const files = pending;
    setPending([]);
    void uploads.start(files, audiences, linked).then(() => toast.success(t(`${K}.filesAdded`, { count: files.length })));
  };
  const submitSource = (source: NewSource) => {
    if (!guardAudiences()) return;
    create.mutate(withAgents(source, linked), {
      onSuccess: () => {
        toast.success(successMessage(source, t, pages));
        onOpenChange(false);
      },
      onError: (caught) => {
        if (isPlanGateError(caught)) onPlanGate(caught);
        toast.error(message(caught));
      },
    });
  };
  const panel = { audiences, onSubmit: submitSource };
  const showDone = tab === "files" && pending.length === 0 && uploads.items.length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        title={t(`${K}.title`)}
        description={t(`${K}.description`)}
        closeLabel={t("common.close")}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <StorageLeft />
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { onOpenChange(false); }}>
                {t(`${K}.cancel`)}
              </Button>
              <Button type="submit" form={formIdOf(tab)} disabled={tab === "drive"} loading={create.isPending}>
                <Icon name="check" />
                {showDone ? t(`${K}.done`) : t(`${K}.submit`)}
              </Button>
            </div>
          </div>
        }
      >
        <Tabs value={tab} onValueChange={(next) => { setTab(next as AddTab); setError(undefined); }}>
          <TabsList className="-mx-5 -mt-4 mb-4 overflow-x-auto px-3">
            {TABS.map((item) => (
              <TabsTrigger key={item.value} value={item.value} className="flex items-center gap-1.5 whitespace-nowrap">
                <Icon name={item.icon} className="size-4" />
                {t(`${K}.${item.label}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            <TabsContent value="files">
              <FilesPanel formId={formIdOf("files")} pending={pending} onPendingChange={(files) => { setPending(files); setError(undefined); }} uploads={uploads.items} sources={sources} error={error} onSubmit={submitFiles} onRetry={(key) => void uploads.retry(key)} />
            </TabsContent>
            <TabsContent value="website">
              <WebsitePanel formId={formIdOf("website")} onDiscovered={setPages} {...panel} />
            </TabsContent>
            <TabsContent value="qa">
              <QaPanel formId={formIdOf("qa")} {...panel} />
            </TabsContent>
            <TabsContent value="text">
              <TextPanel formId={formIdOf("text")} {...panel} />
            </TabsContent>
            <TabsContent value="drive">
              <div className="rounded-lg border border-line">
                <EmptyState icon="database" headingLevel={3} title={t(`${K}.driveTitle`)} description={t(`${K}.driveBody`)} action={<Button variant="secondary" onClick={() => { setTab("files"); }}>{t(`${K}.driveCta`)}</Button>} />
              </div>
            </TabsContent>
            {tab === "drive" ? null : (
              <div className="space-y-4">
                <AudiencePicker value={audiences} onChange={(next) => { setChosen(next); setAudienceError(undefined); }} error={audienceError} />
                <p className="hint -mt-2">
                  {tab === "website" ? t(`${K}.accessWebsiteHint`, { label: labelOf(PUBLIC_AUDIENCE) }) : t(`${K}.accessPrivateHint`, { label: labelOf(MEMBERS_AUDIENCE), everyone: labelOf(PUBLIC_AUDIENCE) })}
                </p>
                {exposesToGuests(tab, audiences) ? (
                  <p role="status" className="flex items-start gap-2 rounded-md bg-accent-soft p-3 text-xs text-accent-ink" data-testid="guest-caution">
                    <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
                    {t(`${K}.guestCaution`)}
                  </p>
                ) : null}
                {canPickAgents ? <AgentPicker value={linkIds} onChange={setLinkIds} /> : null}
              </div>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
